import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import PasswordInput from '../../components/PasswordInput';
import Captcha from '../../components/Captcha';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

export default function AdminLogin() {
  const { settings } = useSettings();
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: localStorage.getItem('ca_admin_saved_email') || '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(Boolean(localStorage.getItem('ca_admin_saved_email')));
  const [agreeSecurity, setAgreeSecurity] = useState(true);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.email.trim()) {
      newErrors.email = 'Please enter authorized admin email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!form.password) {
      newErrors.password = 'Please enter master password';
    }

    if (!isCaptchaValid) {
      newErrors.captcha = 'Please enter the exact 5-character Captcha code';
    }

    if (!agreeSecurity) {
      newErrors.agree = 'Please confirm admin authorization to proceed';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all highlighted required fields');
      return;
    }

    setErrors({});

    try {
      const data = await login(form.email, form.password);
      if (data.accountType !== 'admin') {
        toast.error('This account does not have admin privileges');
        return;
      }

      if (rememberMe) {
        localStorage.setItem('ca_admin_saved_email', form.email);
      } else {
        localStorage.removeItem('ca_admin_saved_email');
      }

      toast.success('Authenticated successfully. Welcome to Super Admin!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Admin authentication failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#120A06] via-[#1C100A] to-[#251710] flex items-center justify-center px-4 py-12 relative overflow-hidden text-[#FAF8F5]">
      {/* Background Decorative Circles */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#C49A45]/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#C49A45]/5 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md bg-[#23160F]/90 backdrop-blur-xl border border-[#3D2C22] rounded-2xl p-6 sm:p-8 shadow-2xl space-y-6 relative z-10">
        
        {/* Header */}
        <div className="text-center space-y-3">
          <Link to="/" className="inline-block">
            <img
              src={resolveMedia(settings?.logo || DEFAULT_LOGO)}
              alt={settings?.siteName || 'Chandana Aura'}
              className="h-11 mx-auto object-contain brightness-110"
            />
          </Link>
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#C49A45]/15 border border-[#C49A45]/30 text-[#C49A45] text-[10px] font-bold uppercase tracking-widest mb-1.5">
              <span>🔒</span> Super Admin Console
            </div>
            <h1 className="font-heading text-2xl font-bold text-white tracking-wide">
              Secure Staff Portal
            </h1>
            <p className="text-xs text-[#A8988D]">
              Enter authorized administrative credentials
            </p>
          </div>
        </div>

        {/* Login Form with noValidate */}
        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#E5D7CC] mb-1.5">
              Admin Email <span className="text-[#C49A45]">*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                autoComplete="email"
                placeholder="admin@chandanaura.com"
                className={`w-full h-11 px-3.5 pl-10 text-xs rounded-lg border transition-all font-medium ${
                  errors.email
                    ? 'border-red-500 bg-red-950/40 text-white focus:border-red-500 focus:ring-1 focus:ring-red-500'
                    : 'border-[#4A382D] bg-[#1A100A] text-white placeholder-[#78675C] focus:border-[#C49A45] focus:ring-1 focus:ring-[#C49A45]'
                } focus:outline-none`}
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                }}
              />
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A8988D]">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </span>
            </div>
            {errors.email && (
              <p className="text-[11px] font-semibold text-red-400 mt-1 flex items-center gap-1">
                <span>⚠</span> {errors.email}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-[#E5D7CC]">
                Master Password <span className="text-[#C49A45]">*</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-[11px] font-bold text-[#C49A45] hover:underline"
              >
                Reset Key?
              </Link>
            </div>
            <PasswordInput
              autoComplete="current-password"
              placeholder="••••••••"
              value={form.password}
              error={Boolean(errors.password)}
              onChange={(e) => {
                setForm({ ...form, password: e.target.value });
                if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
              }}
            />
            {errors.password && (
              <p className="text-[11px] font-semibold text-red-400 mt-1 flex items-center gap-1">
                <span>⚠</span> {errors.password}
              </p>
            )}
          </div>

          {/* Captcha Security Verification */}
          <div className="pt-1">
            <Captcha
              error={Boolean(errors.captcha)}
              onValidate={(valid) => {
                setIsCaptchaValid(valid);
                if (valid && errors.captcha) setErrors((prev) => ({ ...prev, captcha: '' }));
              }}
            />
            {errors.captcha && (
              <p className="text-[11px] font-semibold text-red-400 mt-1 flex items-center gap-1">
                <span>⚠</span> {errors.captcha}
              </p>
            )}
          </div>

          {/* Remember Me & Security Policy Agreement */}
          <div className="space-y-2.5 pt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-[#4A382D] bg-[#1A100A] text-[#C49A45] focus:ring-[#C49A45] cursor-pointer accent-[#C49A45]"
              />
              <span className="text-xs text-[#CFC3BA] font-medium">Remember admin identity</span>
            </label>

            <div>
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agreeSecurity}
                  onChange={(e) => {
                    setAgreeSecurity(e.target.checked);
                    if (e.target.checked && errors.agree) setErrors((prev) => ({ ...prev, agree: '' }));
                  }}
                  className={`w-4 h-4 mt-0.5 rounded border bg-[#1A100A] text-[#C49A45] focus:ring-[#C49A45] cursor-pointer accent-[#C49A45] ${
                    errors.agree ? 'border-red-500 ring-1 ring-red-500' : 'border-[#4A382D]'
                  }`}
                />
                <span className="text-xs text-[#CFC3BA]">
                  I confirm I am an authorized store operator & agree to Chandana Aura's{' '}
                  <Link to="/privacy-policy" target="_blank" className="font-bold text-[#C49A45] hover:underline">
                    Security Policies
                  </Link>.
                </span>
              </label>
              {errors.agree && (
                <p className="text-[11px] font-semibold text-red-400 mt-1 pl-6 flex items-center gap-1">
                  <span>⚠</span> {errors.agree}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-[#C49A45] to-[#B08736] hover:from-[#B08736] hover:to-[#9D772D] text-white font-bold text-xs uppercase tracking-widest rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <span>VERIFYING ACCESS...</span>
            ) : (
              <>
                <span>ENTER ADMIN CONSOLE</span>
                <span>→</span>
              </>
            )}
          </button>
        </form>

        {/* Back to Storefront Link */}
        <div className="pt-4 border-t border-[#3D2C22] text-center">
          <Link
            to="/"
            className="text-xs font-bold text-[#A8988D] hover:text-[#C49A45] transition-colors inline-flex items-center gap-1.5"
          >
            <span>←</span> Return to Public Storefront
          </Link>
        </div>

      </div>
    </div>
  );
}
